/**
 * Form validation
 * Takes care of showing the invalid elements for forms
 * The validation messages are added right after the control element and their
 * text is the one provided by the native implementation of the validation api
 */
class Validation {
    /**
     * Constructor
     */
    constructor(form) {
        this.form = form;
        this.template = document.createElement('span');
        this.template.classList.add('validationmessage');

        this.form.Validation = this;

        // remove the default validation, as it's not always preventing submit (see Safari 10 bug)
        this.form.noValidate = true;

        this.registerEvents();
    }

    /**
     * Register events
     */
    registerEvents() {
        this.form.addEventListener('input', delegate('input, textarea, select', (e) => this.validateElement(e.delegateTarget, false)));
        this.form.addEventListener('Validation:validate', delegate('input, textarea, select', (e) => this.validateElement(e.delegateTarget)));

        // Check the validation on form submit
        this.form.addEventListener('submit', (e) => {
            if (!this.validateForm()) {
                e.preventDefault();
            }
        });
    }

    /**
     * Validate element
     * @param Element formElement
     * @return bool
     */
    validateElement(element, showMessage = true) {
        let messageElement = this.getMessageElement(element);

        if (element.checkValidity()) {
            if (document.body.contains(messageElement)) {
                messageElement.setAttribute('hidden', true);
            }
            return true;
        }

        if (showMessage) {
            if (!document.body.contains(messageElement)) {
                element.parentNode.appendChild(messageElement);
            }


            let message = element.validationMessage;

            if (element.validity.patternMismatch && !element.validity.typeMismatch && element.title) {
                message = (element.validationMessage.includes(element.title)) ? element.validationMessage : element.validationMessage + ' ' + element.title;
            }

            this.showMessage(element, message);
        }

        return false;
    }


    /**
     * Validate form
     * @return bool
     */
    validateForm() {
        let firstInvalidElement;

        // if the form is valid, return (nothing to do)
        if (this.form.checkValidity()) {
            // hide all validation messages
            Array.from(this.form.querySelectorAll('.validationmessage')).forEach((messageElement) => messageElement.setAttribute('hidden', true));
            return true;
        }

        Array.prototype.forEach.call(this.form.elements, (element) => {
            if (this.validateElement(element)) return;

            // remember the first invalid element so we can scroll it into view
            if (!firstInvalidElement) {
                firstInvalidElement = element;
            }
        });

        if (!Validation.inViewport(firstInvalidElement)) {
            firstInvalidElement.scrollIntoView({behavior: 'smooth'});
        }

        return false;
    }


    /**
     * Show message
     * @param {Element} element
     * @param {String} message
     */
    showMessage(element, message) {
        let messageElement = this.getMessageElement(element);
        messageElement.textContent = message;

        if (!document.body.contains(messageElement)) {
            element.parentNode.appendChild(messageElement);
        }
        setTimeout(() => messageElement.removeAttribute('hidden'), 200);
    }

    /**
     * Get message element for a control
     * It always return an Element
     * @param {Element} element
     * @return {Element} message element
     */
    getMessageElement(element) {
        let messageElement = element.parentNode.lastElementChild;
        let templateClone;
        messageElement = (messageElement && messageElement.matches('.validationmessage')) ? messageElement : null;

        if (!messageElement) {
            templateClone = this.template.cloneNode(true);
            templateClone.textContent = element.validationMessage;
            templateClone.setAttribute('hidden', true);
            messageElement = templateClone;
        }

        return messageElement;
    }

    /**
     * In viewport
     * Utility method
     * @param {Element} element
     * @return Boolean
     */
    static inViewport(element) {
        let rectangularData = element.getBoundingClientRect();
        return rectangularData.bottom > 0 &&
            rectangularData.right > 0 &&
            rectangularData.left < window.innerWidth &&
            rectangularData.top < window.innerHeight;
    }
}

class ValidationHandler {

    /**
     * Constructor
     */
    constructor() {
        let formElements = document.querySelectorAll('form');
        Array.from(formElements).forEach((formElement) => ValidationHandler.setup(formElement));

        // mutation observer
        let observer = new MutationObserver(this.observeMutations);
        observer.observe(document.body, {childList: true, subtree: true});
    }

    /**
     * Setup
     * @param {Form} formElement
     */
    static setup(formElement) {
        if (formElement.Validation) return false;
        new Validation(formElement);
    }


    /**
     * Observe mutations
     * @param {Object} mutationsList
     */
    observeMutations(mutationsList) {
        for (var mutation of mutationsList) {
            if (!mutation.addedNodes.length) continue;
            let forms = mutation.target.querySelectorAll('form');
            if (!forms.length) continue;

            Array.from(forms).forEach((formElement) => ValidationHandler.setup(formElement));
        }
    }
}

new ValidationHandler;
